import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import assistantFile from "../../api/assistFilesApi";
import useFeatures from "../../hooks/useFeatures";
import FeatureGate from "../FeatureGate/FeatureGate";
import './FileManager.css';
import { setStorageFiles } from "../ChatApp/chatSlice";
import { useDispatch } from "react-redux";

const FileManager = ({ storageId, userId }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { canUseStorage, loading: featuresLoading, loadFeatures } = useFeatures();

    const [localFiles, setLocalFiles] = useState([]);
    const [uploadedFiles, setUploadedFiles] = useState([]);

    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({});

    const [totalUploadedSize, setTotalUploadedSize] = useState(0); // в байтах
    // const MAX_SIZE = 500 * 1024 * 1024;
    // const MAX_FILES = 10
    const [MAX_SIZE, setMaxSize] = useState(0);
    const [MAX_FILES, setMaxFiles] = useState(0);
    const ALLOWED_EXTENSIONS = ['pdf', 'txt', 'doc', 'docx', 'xls', 'csv', 'rtf'];


    const fileInputRef = useRef(null);

    // Загружаем features при монтировании
    useEffect(() => {
        loadFeatures();
    }, [loadFeatures]);

    useEffect(() => {
        if (!storageId) return;
        const fetchUploadedFiles = async () => {
            try {
                const res = await assistantFile.listStorageFiles(storageId, userId);
                setUploadedFiles(res.data || []);
                dispatch(setStorageFiles(res.data));
            } catch (err) {
                console.error("Ошибка при загрузке файлов:", err);
            }
        };

        fetchUploadedFiles();
    }, [storageId]);

    useEffect(() => {
        if (!userId) return;

        const fetchSize = async () => {
            try {
                const res = await assistantFile.getTotalSizeOfFiles(userId);
                //console.log('getTotalSizeOfFiles', res.data.totalSize)
                //console.log('getMaxFilesForStorage', res.data.filelimit)
                //console.log('getMaxMemoryForStorage', res.data.memoryLimit)
                setMaxSize(res.data.memoryLimit || 0)
                setMaxFiles(res.data.filelimit || 0)
                setTotalUploadedSize(res.data.totalSize || 0);
            } catch (err) {
                console.error("Ошибка при получении общего объема:", err);
            }
        };

        fetchSize();
    }, [userId]);


    const handleFileChange = (e) => {
        const selected = Array.from(e.target.files);
        const selectedSize = selected.reduce((sum, f) => sum + f.size, 0);
        const localFilesSize = localFiles.reduce((sum, f) => sum + f.size, 0)
        const newTotal =
            Number(totalUploadedSize) +
            Number(localFilesSize) +
            Number(selectedSize);

        //console.log(newTotal, totalUploadedSize, localFilesSize, selectedSize)
        const totalFileCount = localFiles.length + selected.length;
        //console.log('totalFileCount', totalFileCount)
        if (totalFileCount > MAX_FILES) {
            alert(`Нельзя загружать более ${MAX_FILES} файлов. Удалите лишние перед загрузкой.`);
            return;
        }


        if (newTotal > MAX_SIZE) {
            alert(`Общий объем файлов превышает ${MAX_SIZE % 1024 % 1024} МБ. Удалите часть файлов перед загрузкой.`);
            return;
        }

        // Проверка форматов
        const invalidFiles = selected.filter((file) => {
            const ext = file.name.split('.').pop().toLowerCase();
            return !ALLOWED_EXTENSIONS.includes(ext);
        });

        if (invalidFiles.length > 0) {
            alert(
                `Некоторые файлы имеют неподдерживаемый формат:\n${invalidFiles
                    .map((f) => f.name)
                    .join(', ')}.\nРазрешённые форматы: ${ALLOWED_EXTENSIONS.join(', ')}.`
            );
            return;
        }

        setLocalFiles((prev) => [...prev, ...selected]);
    };

    const removeLocalFile = (index) => {
        setLocalFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const removeUploadedFile = async (fileId) => {
        const fileObj = uploadedFiles.find(f => f.file_openai_id === fileId);
        if (fileObj?.file_size) {
            setTotalUploadedSize(prev => prev - fileObj.file_size);
        }
        try {
            await assistantFile.deleteFileFromStorage(fileId, storageId, userId);
            const updatedFiles = uploadedFiles.filter((file) => file.file_openai_id !== fileId);
            setUploadedFiles(updatedFiles);
            dispatch(setStorageFiles(updatedFiles)); // Обновляем Redux в реальном времени
        } catch (err) {
            console.error("Ошибка при удалении файла:", err);
        }
    };

    // const uploadFiles = async () => {
    //     if (!storageId || !userId || localFiles.length === 0) return;
    //     try {
    //         const formData = new FormData();
    //         localFiles.forEach((file) => formData.append("files", file, file.name));

    //         const fileNames = localFiles.map(file => file.name).join(", ");
    //         formData.append("files_name", fileNames);

    //         await assistantFile.sendFilesToStorage(formData, storageId, userId);
    //         setLocalFiles([]);

    //         const res = await assistantFile.listStorageFiles(storageId);
    //         setUploadedFiles(res.data || []);
    //     } catch (err) {
    //         console.error("Ошибка при загрузке файлов:", err);
    //     }
    // };

    const uploadFiles = async () => {
        if (!storageId || !userId || localFiles.length === 0) return;

        setIsUploading(true);
        setUploadProgress({});

        for (const file of localFiles) {
            const formData = new FormData();
            formData.append("files", file, file.name);
            formData.append("files_name", file.name);

            try {
                await new Promise((resolve, reject) => {
                    const xhr = new XMLHttpRequest();
                    xhr.open("POST", `${process.env.REACT_APP_API_URL}/file/upload/${storageId}/${userId}`);

                    xhr.upload.onprogress = (event) => {
                        if (event.lengthComputable) {
                            const percent = Math.round((event.loaded / event.total) * 100);
                            setUploadProgress(prev => ({
                                ...prev,
                                [file.name]: percent
                            }));
                        }
                    };

                    xhr.onload = () => {
                        if (xhr.status === 200) resolve();
                        else reject(xhr.responseText);
                    };

                    xhr.onerror = () => reject("Ошибка соединения");

                    xhr.send(formData);
                });
            } catch (err) {
                console.error(`Ошибка при загрузке файла ${file.name}:`, err);
            }
        }

        //console.log('localFiles', localFiles)
        const uploadedSize = localFiles.reduce((sum, f) => sum + parseInt(f.size), 0);
        //console.log('uploadedSize', uploadedSize, totalUploadedSize)
        setTotalUploadedSize(prev => Number(prev) + Number(uploadedSize));


        setIsUploading(false);
        setLocalFiles([]);

        try {
            const res = await assistantFile.listStorageFiles(storageId, userId);
            setUploadedFiles(res.data || []);
            dispatch(setStorageFiles(res.data || [])); // Обновляем Redux в реальном времени
        } catch (err) {
            console.error("Ошибка при обновлении списка файлов:", err);
        }
    };


    const handleUpgradeClick = () => {
        navigate('/subscription');
    };

    // Показываем загрузку пока features грузятся
    if (featuresLoading) {
        return <div className="file-manager">Загрузка...</div>;
    }

    // Если нет доступа к хранилищу - показываем FeatureGate
    if (!canUseStorage) {
        return (
            <FeatureGate
                hasAccess={false}
                featureName="Хранилище файлов"
                requiredPlan="Pro"
                onUpgradeClick={handleUpgradeClick}
            />
        );
    }

    return (
        <div className="file-manager">
            <div className="file-manager-header">
                <button onClick={() => fileInputRef.current.click()}>
                    Вы можете прикрепить не более 10 файлов за один раз (pdf, txt, doc/docx, csv, rtf)
                </button>
                <p>
                    Занято: {(totalUploadedSize / 1024 / 1024).toFixed(2)} МБ/{(MAX_SIZE / 1024 / 1024).toFixed(2)} МБ
                </p>
            </div>

            <input
                type="file"
                multiple
                ref={fileInputRef}
                onChange={handleFileChange}
                style={{ display: "none" }}
            />

            {/* {localFiles.length > 0 && (
                <div className="file-list">
                    <strong>Выбранные файлы:</strong>
                    {localFiles.map((file, index) => (
                        <div key={index} className="file-item">
                            <span>{file.name}</span>
                            <button onClick={() => removeLocalFile(index)}>❌</button>
                        </div>
                    ))}
                    <button onClick={uploadFiles}>Загрузить</button>
                </div>
            )} */}
            {localFiles.length > 0 && (
                <div className="file-list">
                    <strong>Выбранные файлы:</strong>
                    {localFiles.map((file, index) => (
                        <div key={index} className="file-item">
                            <span>{file.name}</span>
                            {isUploading ? (
                                <span style={{ marginLeft: 8 }}>
                                    {uploadProgress[file.name] || 0}%
                                </span>
                            ) : (
                                <button onClick={() => removeLocalFile(index)}>❌</button>
                            )}
                        </div>
                    ))}
                    {!isUploading ? (
                        <button onClick={uploadFiles}>Загрузить</button>
                    ) : (
                        <span>Загрузка...</span>
                    )}
                </div>
            )}

            {uploadedFiles.length > 0 && (
                <div className="uploaded-file-list">
                    <strong>Загруженные файлы:</strong>
                    {uploadedFiles.map((file, index) => (
                        <div key={index} className="file-item">
                            <span>{file.filename}</span>
                            <button onClick={() => removeUploadedFile(file.file_openai_id)}>❌</button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default FileManager;